import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone, RefreshCw } from 'lucide-react';
import InfoTooltip from './onboarding/InfoTooltip';
import {
  getAppleSearchAdsAppSummary,
  triggerAppleSearchAdsSync,
  type AppleSearchAdsSummary,
} from '../api/client';
import { AppError } from '../api/request';
import { useApplication } from '../context/ApplicationContext';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import AppPageLayout from './AppPageLayout';

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function formatMoney(v: number, currency?: string): string {
  const amount = v.toFixed(2);
  return currency ? `${amount} ${currency}` : amount;
}

// Bu sayfa yalnızca OKUMA amaçlıdır: Apple Search Ads bağlantısı hesap (kullanıcı) seviyesindedir ve
// panelPath('/apple-search-ads') sayfasından kurulur. Burada yalnızca o hesabın bu uygulamayı tanıtan
// kampanyalarla filtrelenmiş görünümü gösterilir (App Store Connect üzerinden otomatik eşlenir).
export default function AppleSearchAdsPage() {
  const { appId } = useParams<{ appId: string }>();
  const { t } = useTranslation();
  const { showError } = useError();
  // ApplicationProvider already loads GET /applications/:id; reuse it.
  const { app: contextApp, loading, error: contextError } = useApplication();
  const app = appId && contextApp?.id === appId && contextApp.type === 'mobil_app' ? contextApp : null;
  const error =
    contextError ??
    (contextApp && contextApp.id === appId && contextApp.type !== 'mobil_app'
      ? t('appleSearchAds.mobilOnly')
      : null);

  const [summary, setSummary] = useState<AppleSearchAdsSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!appId) return;
    setLoadingSummary(true);
    try {
      const s = await getAppleSearchAdsAppSummary(appId);
      setSummary(s);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.loadError'));
    } finally {
      setLoadingSummary(false);
    }
  }, [appId, showError, t]);

  useEffect(() => {
    if (app) void loadSummary();
  }, [app, loadSummary]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await triggerAppleSearchAdsSync();
      await loadSummary();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const accountHref = panelPath('/apple-search-ads');

  return (
    <AppPageLayout app={app} loading={loading} error={error} pageTitle={t('appleSearchAds.pageTitle')}>
      {error && !app && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          {error}
        </div>
      )}

      {app && loadingSummary && !summary && (
        <div className="text-center text-gray-500 text-sm py-8">{t('common.loading')}</div>
      )}

      {app && summary && !summary.connected && (
        <div className="max-w-xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Megaphone className="h-10 w-10 text-primary-600 dark:text-primary-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('appleSearchAds.heading')}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('appleSearchAds.accountLevelHint')}</p>
              <Link
                to={accountHref}
                className="mt-4 inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2.5 text-sm font-semibold shadow-sm"
              >
                {t('appleSearchAds.goToConnect')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {app && summary && summary.connected && !summary.appStoreAppResolved && (
        <div className="max-w-xl rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-5">
          <p className="text-sm text-amber-800 dark:text-amber-200">{t('appleSearchAds.appNotResolved')}</p>
          <Link
            to={panelPath(`/applications/${appId}`)}
            className="mt-3 inline-flex items-center justify-center rounded-lg border border-amber-300 dark:border-amber-800 px-3 py-1.5 text-sm font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            {t('appleSearchAds.goToAppStoreConnect')}
          </Link>
        </div>
      )}

      {app && summary && summary.connected && summary.appStoreAppResolved && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('appleSearchAds.rangeLabel', { days: 30 })}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleSyncNow()}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('common.loading') : t('appleSearchAds.syncNow')}
              </button>
              <Link
                to={accountHref}
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('appleSearchAds.manageConnection')}
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label={t('appleSearchAds.totalSpend')} value={formatMoney(summary.totalSpend, summary.currency)} tooltip={t('onboarding.appleSearchAds.spendHelp')} />
            <StatTile label={t('appleSearchAds.totalInstalls')} value={String(summary.totalInstalls)} tooltip={t('onboarding.appleSearchAds.installsHelp')} />
            <StatTile label={t('appleSearchAds.avgCPA')} value={formatMoney(summary.avgCPA, summary.currency)} tooltip={t('onboarding.appleSearchAds.cpaHelp')} />
            <StatTile label={t('appleSearchAds.avgTTR')} value={formatPercent(summary.avgTTR)} tooltip={t('onboarding.appleSearchAds.ttrHelp')} />
            <StatTile label={t('appleSearchAds.avgCR')} value={formatPercent(summary.avgCR)} tooltip={t('onboarding.appleSearchAds.crHelp')} />
            {summary.roas !== undefined && summary.roas !== null && (
              <StatTile label={t('appleSearchAds.roas')} value={`${summary.roas.toFixed(2)}x`} highlight tooltip={t('onboarding.appleSearchAds.roasHelp')} />
            )}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('appleSearchAds.campaignsTitle')}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2 font-medium">{t('appleSearchAds.colCampaign')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colImpressions')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colTaps')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colInstalls')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colSpend')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colCPA')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colTTR')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(summary.campaigns ?? []).length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {t('appleSearchAds.noData')}
                      </td>
                    </tr>
                  )}
                  {(summary.campaigns ?? []).map(c => (
                    <tr key={c.campaignId}>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{c.campaignName || c.campaignId}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{c.impressions}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{c.taps}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{c.installs}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatMoney(c.spend, c.currency)}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatMoney(c.cpa, c.currency)}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatPercent(c.ttr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('appleSearchAds.topKeywordsTitle')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('appleSearchAds.topKeywordsSubtitle')}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2 font-medium">{t('appleSearchAds.colKeyword')}</th>
                    <th className="px-4 py-2 font-medium">{t('appleSearchAds.colCampaign')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colInstalls')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colCPA')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colCR')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(summary.topKeywords ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {t('appleSearchAds.noData')}
                      </td>
                    </tr>
                  )}
                  {(summary.topKeywords ?? []).map(k => (
                    <tr key={`${k.campaignId}-${k.keywordId}`}>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{k.keyword}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{k.campaignName || k.campaignId}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{k.installs}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatMoney(k.cpa)}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatPercent(k.cr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AppPageLayout>
  );
}

function StatTile({ label, value, highlight, tooltip }: { label: string; value: string; highlight?: boolean; tooltip?: string }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight
          ? 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
      }`}
    >
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}
